import React, { useEffect, useState, memo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Box, Paper, Typography, Button, Avatar, TextField, IconButton, Chip } from '@mui/material';
import { getHints, getCommentsByHint, postCommentToHint } from '../services/api';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ChatHintList from './ChatHintList';
import TimerLoader from './TimerLoader';
import PushNotificationSettings from './PushNotificationSettings';

interface Hint {
    id: number;
    text: string;
    image?: string;
    latitude?: number;
    longitude?: number;
    radius?: number;
    comments?: string[];
}

interface Comment {
    id: number;
    text: string;
    created_by_name: string; // ← ここをcreated_by_nameに
    created_at: string;
    hint: number;
}

const HEADER_HEIGHT = 64; // AppBarの高さ(px)。必要に応じて調整

const EventRoom: React.FC = () => {
    const { token, refreshTokenFunction, logout } = useAuth();
    const navigate = useNavigate();
    // JST 21:00 (UTC 12:00) に終了
    const getEndTime = () => {
        const now = new Date();
        const jstYear = now.getUTCFullYear();
        const jstMonth = now.getUTCMonth();
        const jstDate = now.getUTCDate();
        const utcHour = 21 - 9; // JST 21:00 = UTC 12:00
        let end = new Date(Date.UTC(jstYear, jstMonth, jstDate, utcHour, 0, 0));
        if (now > end) {
            end = new Date(Date.UTC(jstYear, jstMonth, jstDate + 1, utcHour, 0, 0));
        }
        return end;
    };

    const [hints, setHints] = useState<Hint[]>([]);
    const [latestHint, setLatestHint] = useState<Hint | null>(null);

    // コメント入力用のstate（ヒントごとに管理）
    const [commentInputs, setCommentInputs] = useState<{ [key: number]: string }>({});
    const [comments, setComments] = useState<{ [key: number]: Comment[] }>({});
    const [showComments, setShowComments] = useState<{ [key: number]: boolean }>({});

    // エラー管理
    const [errorCount, setErrorCount] = useState<number>(0);
    const [lastHintIds, setLastHintIds] = useState<number[]>([]);
    const [titleTapCount, setTitleTapCount] = useState(0);
    const [clearMode, setClearMode] = useState(false);
    const longPressTimer = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (!token) {
            navigate('/login', { replace: true });
        }
    }, [token, navigate]);

    // コメント一覧をヒントごとに取得（差分更新対応）
    const fetchCommentsForHints = async (hints: Hint[], forceUpdate: boolean = false) => {
        if (!token) return;

        // 新しいヒントのみ、またはforceUpdateがtrueの場合は全て更新
        const hintsToUpdate = forceUpdate
            ? hints
            : hints.filter(hint => !lastHintIds.includes(hint.id));

        if (hintsToUpdate.length === 0) return;

        const newComments: { [key: number]: Comment[] } = { ...comments };

        await Promise.all(
            hintsToUpdate.map(async (hint) => {
                try {
                    const res = await getCommentsByHint(token, hint.id);
                    newComments[hint.id] = res.data;
                } catch (error: any) {
                    // 401の場合はリフレッシュトークンで再取得を試みる
                    if (error.response && error.response.status === 401) {
                        try {
                            const newToken = await refreshTokenFunction();
                            if (newToken) {
                                const retryRes = await getCommentsByHint(newToken, hint.id);
                                newComments[hint.id] = retryRes.data;
                                return;
                            }
                        } catch (refreshError) { }
                    }
                    newComments[hint.id] = [];
                }
            })
        );
        setComments(newComments);
        setLastHintIds(hints.map(h => h.id));
    };

    // ヒントとコメントを取得
    useEffect(() => {
        if (!token) return;

        let timeoutId: NodeJS.Timeout;

        const fetchHintsAndComments = async () => {
            // ページが非アクティブの場合はスキップ
            if (document.visibilityState === 'hidden') {
                scheduleNextFetch();
                return;
            }

            try {
                const response = await getHints(token);
                const fetchedHints = response.data;
                setHints(fetchedHints);
                setLatestHint(fetchedHints[0] || null);
                await fetchCommentsForHints(fetchedHints);
                setErrorCount(0); // 成功時はエラーカウントをリセット
            } catch (error: any) {
                setErrorCount(prev => prev + 1);
                // 401の場合はリフレッシュトークンで再取得を試みる
                if (error.response && error.response.status === 401) {
                    try {
                        const newToken = await refreshTokenFunction();
                        if (newToken) {
                            const retryResponse = await getHints(newToken);
                            const fetchedHints = retryResponse.data;
                            setHints(fetchedHints);
                            setLatestHint(fetchedHints[0] || null);
                            await fetchCommentsForHints(fetchedHints);
                            setErrorCount(0);
                            scheduleNextFetch();
                            return;
                        }
                    } catch (refreshError) { }
                }
                console.error('ヒントの取得に失敗しました:', error);
                if (errorCount >= 3) {
                    logout();
                    return;
                }
            }
            scheduleNextFetch();
        };

        const scheduleNextFetch = () => {
            // 指数バックオフ: エラー回数に応じて間隔を延長
            const baseInterval = 10000; // 10秒
            const interval = Math.min(baseInterval * Math.pow(2, errorCount), 60000); // 最大60秒
            timeoutId = setTimeout(fetchHintsAndComments, interval);
        };

        // 初回実行
        fetchHintsAndComments();

        // ページの可視性変更を監視
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                // ページがアクティブになったら即座に取得
                clearTimeout(timeoutId);
                fetchHintsAndComments();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            clearTimeout(timeoutId);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [token, errorCount]);

    // ヒントが変わったらコメントも再取得
    useEffect(() => {
        if (hints.length > 0) {
            fetchCommentsForHints(hints);
        }
    }, [hints, token]);

    // コメント送信
    const handleCommentSend = async (hintId: number) => {
        const text = commentInputs[hintId]?.trim();
        if (!text || !token) return;
        try {
            await postCommentToHint(token, hintId, text);
            setCommentInputs((prev) => ({
                ...prev,
                [hintId]: '',
            }));
            // 送信したヒントのコメントのみ再取得
            const targetHint = hints.find(h => h.id === hintId);
            if (targetHint) {
                await fetchCommentsForHints([targetHint], true);
            }
        } catch (error: any) {
            // 401の場合はリフレッシュトークンで再取得を試みる
            if (error.response && error.response.status === 401) {
                try {
                    const newToken = await refreshTokenFunction();
                    if (newToken) {
                        await postCommentToHint(newToken, hintId, text);
                        setCommentInputs((prev) => ({
                            ...prev,
                            [hintId]: '',
                        }));
                        const targetHint = hints.find(h => h.id === hintId);
                        if (targetHint) {
                            await fetchCommentsForHints([targetHint], true);
                        }
                        return;
                    }
                } catch (refreshError) { }
            }
            alert('コメント送信に失敗しました');
        }
    };

    // タイトルタップカウント
    const handleTitleTap = () => {
        setTitleTapCount((prev) => prev + 1);
    };

    // ホームに戻る長押し
    const handleHomeButtonDown = () => {
        if (titleTapCount >= 5) {
            longPressTimer.current = setTimeout(() => {
                setClearMode(true);
            }, 1200); // 1.2秒長押し
        }
    };
    const handleHomeButtonUp = () => {
        if (longPressTimer.current) {
            clearTimeout(longPressTimer.current);
        }
    };

    // ギミック用: 意味のないアイコン長押し
    const handleGimmickButtonDown = () => {
        if (titleTapCount >= 5) {
            longPressTimer.current = setTimeout(() => {
                setClearMode(true);
            }, 1200); // 1.2秒長押し
        }
    };
    const handleGimmickButtonUp = () => {
        if (longPressTimer.current) {
            clearTimeout(longPressTimer.current);
        }
    };

    return (
        <Box
            sx={{
                marginTop: `${HEADER_HEIGHT + 16}px`,
                padding: 2,
                minHeight: `calc(100vh - ${HEADER_HEIGHT}px)`,
                boxSizing: 'border-box',
                background: 'linear-gradient(135deg, #fff1f8 0%, #ffe3ed 100%)',
            }}
        >
            <Paper
                sx={{
                    padding: 3,
                    maxWidth: 600,
                    margin: '0 auto',
                    marginBottom: 3,
                    background: 'linear-gradient(135deg, #fbc2eb 0%, #fcb69f 100%)',
                    color: '#ad1457',
                    textAlign: 'center',
                    borderRadius: 4,
                    boxShadow: '0 8px 32px rgba(255, 128, 171, 0.10)',
                    border: '1.5px solid #f8bbd0',
                }}
            >
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 2, color: '#d81b60', letterSpacing: 2, textShadow: '0 2px 8px #fff6fb', userSelect: 'none', cursor: 'pointer' }}
                    onClick={handleTitleTap}
                >
                    <span role="img" aria-label="fire">🔥</span>もんクエ<span role="img" aria-label="fire">🔥</span>
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                    <AccessTimeIcon sx={{ mr: 1, fontSize: 28, color: '#f06292' }} />
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#ad1457', textShadow: '0 1px 4px #fff6fb' }}>
                        残り時間
                    </Typography>
                </Box>
                {/* CountdownTimerコンポーネントで置き換え */}
                <TimerLoader clearMode={clearMode} />
            </Paper>
            <Paper
                sx={{
                    padding: 3,
                    maxWidth: 600,
                    margin: '0 auto',
                    background: 'linear-gradient(135deg, #fff1f8 0%, #f8bbd0 100%)', // さらに淡いピンク
                    borderRadius: 3,
                    boxShadow: '0 8px 32px rgba(255, 128, 171, 0.08)',
                    border: '1.5px solid #f8bbd0',
                    backdropFilter: 'blur(10px)'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box
                        sx={{
                            width: 4,
                            height: 32,
                            background: 'linear-gradient(135deg, #f06292 0%, #f8bbd0 100%)',
                            borderRadius: 2,
                            mr: 2
                        }}
                    />
                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: 700,
                            color: '#ad1457',
                            letterSpacing: '-0.025em',
                            textShadow: '0 1px 4px #fff6fb',
                            display: 'flex',
                            alignItems: 'center',
                            userSelect: 'none'
                        }}
                    >
                        もんからのヒント
                        {/* ギミック用吹き出し絵文字アイコン */}
                        <span
                            role="img"
                            aria-label="gimmick"
                            style={{
                                cursor: titleTapCount >= 5 ? 'pointer' : 'default',
                                marginLeft: 8,
                                fontSize: 28,
                                transition: 'opacity 0.3s',
                                opacity: titleTapCount >= 5 ? 1 : 0.4
                            }}
                            onMouseDown={handleGimmickButtonDown}
                            onMouseUp={handleGimmickButtonUp}
                            onMouseLeave={handleGimmickButtonUp}
                            onTouchStart={handleGimmickButtonDown}
                            onTouchEnd={handleGimmickButtonUp}
                        >
                            💬
                        </span>
                    </Typography>
                </Box>
                {hints.length > 0 ? (
                    <ChatHintList
                        hints={hints}
                        comments={comments}
                        commentInputs={commentInputs}
                        showComments={showComments}
                        handleCommentSend={handleCommentSend}
                        setShowComments={setShowComments}
                        setCommentInputs={setCommentInputs}
                    />
                ) : (
                    <Box
                        sx={{
                            textAlign: 'center',
                            py: 6,
                            background: 'linear-gradient(135deg, #f8bbd0 0%, #fff1f8 100%)',
                            borderRadius: 2,
                            border: '2px dashed #f8bbd0',
                            color: '#ad1457',
                        }}
                    >
                        <ChatBubbleOutlineIcon
                            sx={{
                                fontSize: 48,
                                color: '#f06292',
                                mb: 2
                            }}
                        />
                        <Typography
                            variant="body1"
                            sx={{
                                color: '#ad1457',
                                fontWeight: 500,
                                fontSize: '1.1rem',
                                textShadow: '0 1px 4px #fff6fb'
                            }}
                        >
                            ヒントはまだありません
                        </Typography>
                    </Box>
                )}
            </Paper>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button
                    variant="outlined"
                    color="secondary"
                    sx={{ fontWeight: 700, borderRadius: 3, borderColor: '#d81b60', color: '#d81b60', px: 3, py: 1, boxShadow: '0 2px 8px #f8bbd0', '&:hover': { background: '#f8bbd0', borderColor: '#d81b60' } }}
                    onClick={() => navigate('/')}
                    onMouseDown={handleHomeButtonDown}
                    onMouseUp={handleHomeButtonUp}
                    onMouseLeave={handleHomeButtonUp}
                    onTouchStart={handleHomeButtonDown}
                    onTouchEnd={handleHomeButtonUp}
                >
                    ホームに戻る
                </Button>
            </Box>

            {/* Push通知設定 */}
            <PushNotificationSettings />
        </Box>
    );
};

export default EventRoom;